// Client Projects Workspace — Sorting Utilities
// Requirements: 4.2, 4.4, 4.6, 4.7

import type { ClientProjectSummaryDto, ClientSortOption } from '../../../../types/client-workspace';

/**
 * Sorts a list of projects by the given sort option.
 *
 * - Returns a NEW array (never mutates the original).
 * - Sort is stable (equal elements maintain original relative order).
 * - Null/undefined sort fields are placed at the end.
 * - "Approval Needed" places `awaiting_review` projects first, with secondary sort by recent activity.
 *
 * Sort directions:
 *   Most Recent Activity → newest first (descending)
 *   Last Updated → newest first (descending)
 *   Due Date → earliest first (ascending)
 *   Alphabetical → A–Z case-insensitive (ascending)
 *   Recently Delivered → newest first (descending)
 *   Priority → highest number first (descending)
 *   Approval Needed → awaiting_review first, then by recent activity (descending)
 */
export function sortProjects(
  projects: ClientProjectSummaryDto[],
  sortOption: ClientSortOption,
): ClientProjectSummaryDto[] {
  // Return a shallow copy to avoid mutating the original
  const copy = [...projects];

  switch (sortOption) {
    case 'recent_activity':
      return stableSort(copy, (a, b) =>
        compareTimestampsDesc(a.lastActivityAt, b.lastActivityAt),
      );

    case 'last_updated':
      return stableSort(copy, (a, b) =>
        compareTimestampsDesc(a.lastActivityAt, b.lastActivityAt),
      );

    case 'due_date':
      return stableSort(copy, (a, b) =>
        compareNullableTimestampsAsc(a.dueDate, b.dueDate),
      );

    case 'alphabetical':
      return stableSort(copy, (a, b) =>
        compareStringsAsc(a.name, b.name),
      );

    case 'recently_delivered':
      return stableSort(copy, (a, b) =>
        compareNullableTimestampsDesc(a.deliveredAt, b.deliveredAt),
      );

    case 'priority':
      return stableSort(copy, (a, b) =>
        compareNumbersDesc(a.priority, b.priority),
      );

    case 'approval_needed':
      return stableSort(copy, (a, b) => {
        const aIsAwaiting = a.status === 'awaiting_review' ? 0 : 1;
        const bIsAwaiting = b.status === 'awaiting_review' ? 0 : 1;

        // Primary: awaiting_review first
        if (aIsAwaiting !== bIsAwaiting) {
          return aIsAwaiting - bIsAwaiting;
        }

        // Secondary: most recent activity (descending)
        return compareTimestampsDesc(a.lastActivityAt, b.lastActivityAt);
      });

    default:
      // Defensive: unknown sort option returns the copy in original order
      return copy;
  }
}

// --- Internal helpers ---

/**
 * Stable sort implementation.
 * JavaScript's Array.prototype.sort is stable in all modern engines (ES2019+),
 * but we explicitly preserve index-based tie-breaking for correctness guarantees.
 */
function stableSort<T>(
  arr: T[],
  compareFn: (a: T, b: T) => number,
): T[] {
  // Decorate with original index for tie-breaking
  const indexed = arr.map((item, index) => ({ item, index }));

  indexed.sort((a, b) => {
    const result = compareFn(a.item, b.item);
    // If compareFn considers them equal, preserve original order
    if (result === 0) return a.index - b.index;
    return result;
  });

  return indexed.map(({ item }) => item);
}

/**
 * Compare two non-null ISO timestamp strings in descending order (newest first).
 */
function compareTimestampsDesc(a: string, b: string): number {
  const timeA = new Date(a).getTime();
  const timeB = new Date(b).getTime();
  return timeB - timeA;
}

/**
 * Compare two nullable ISO timestamp strings in ascending order (earliest first).
 * Nulls are placed at the end.
 */
function compareNullableTimestampsAsc(
  a: string | null,
  b: string | null,
): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1; // null at end
  if (b === null) return -1; // null at end

  const timeA = new Date(a).getTime();
  const timeB = new Date(b).getTime();
  return timeA - timeB;
}

/**
 * Compare two nullable ISO timestamp strings in descending order (newest first).
 * Nulls are placed at the end.
 */
function compareNullableTimestampsDesc(
  a: string | null,
  b: string | null,
): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1; // null at end
  if (b === null) return -1; // null at end

  const timeA = new Date(a).getTime();
  const timeB = new Date(b).getTime();
  return timeB - timeA;
}

/**
 * Compare two strings in ascending order (A–Z), case-insensitive.
 */
function compareStringsAsc(a: string, b: string): number {
  return a.toLowerCase().localeCompare(b.toLowerCase());
}

/**
 * Compare two numbers in descending order (highest first).
 */
function compareNumbersDesc(a: number, b: number): number {
  return b - a;
}
